import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Truck,
  MapPin,
  Check,
  Clock,
  DollarSign,
  Package,
  Loader2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { DeliveryRouteMap } from "@/components/DeliveryRouteMap";
import { Capacitor } from "@capacitor/core";
import { Geolocation } from "@capacitor/geolocation";
import { enqueue as enqueueOutbox, useOutbox, retryFailed as retryFailedOutbox } from "@/lib/outbox";
import { DriverGPSSender, deliveryMilesKey } from "@/components/DriverGPSSender";
import { IRS_MILEAGE_RATE, MILEAGE_CATEGORY, mileageDeduction } from "@shared/expenses";
import { PermissionsGate } from "@/components/PermissionsGate";
import { DeclineDeliveryDialog } from "@/components/DeclineDeliveryDialog";
import { ConfirmDeliveryDialog } from "@/components/ConfirmDeliveryDialog";
import { PickupStopDialog } from "@/components/PickupStopDialog";

type DriverProfile = {
  id: string;
  vehicleType: string;
  maxMiles: number;
  isActive: boolean;
  totalDeliveries: number;
  totalEarnings: number;
};

type DeliveryStop = {
  id: string;
  sellerName: string;
  city: string;
  state: string;
  lat?: string | number | null;
  lng?: string | number | null;
  status?: string;
  pickedUpAt?: string | null;
  sellerReady?: boolean;
};

type DeliveryRequest = {
  id: string;
  orderId?: string;
  buyerCity: string;
  buyerState: string;
  buyerLat?: string | number | null;
  buyerLng?: string | number | null;
  estimatedMiles: number;
  estimatedMinutes?: number;
  impliedHourlyRate?: number;
  drivingMiles?: number;
  vehicleCost?: number;
  netDriverPay?: number;
  netHourlyRate?: number;
  estimatedStops?: number;
  driverPay: number;
  isFounderDelivery?: boolean;
  deliverByDate?: string;
  deliverBy?: string;
  requestedDeliveryDate?: string | null;
  scheduledFor?: string | null;
  stops: DeliveryStop[];
  status: string;
  orderStatus?: string | null;
};

function formatScheduledFor(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const weekday = d.toLocaleDateString(undefined, { weekday: "short" });
  const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return `${weekday}, ${date}, ${time}`;
}

function isStopPickedUp(stop: DeliveryStop): boolean {
  return stop.status === "picked_up" || Boolean(stop.pickedUpAt);
}

// Meters between two lat/lng points (haversine). Used to detect when the
// driver has arrived at a pickup so the app can prompt them to confirm.
function metersBetween(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// How close the driver must be to a pickup for the "You've arrived" prompt.
const ARRIVAL_RADIUS_M = 120;

function formatDeliverBy(req: DeliveryRequest): string {
  const raw = req.deliverByDate || req.deliverBy;
  if (!raw) return "7 days";
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return "7 days";
    return d.toLocaleDateString();
  } catch {
    return "7 days";
  }
}

// Errors carry the server's machine-readable `code` alongside the prose. The
// distance-mismatch override used to be armed by string-matching the message,
// which meant rewording that sentence would silently strand drivers again.
type ApiError = Error & { code?: string };

async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: "include", ...options });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    // Error bodies are JSON ({"message": "..."}) — surface the message, not
    // the raw JSON, since this string lands directly in a toast.
    let message = text;
    let code: string | undefined;
    try {
      const parsed = JSON.parse(text);
      message = parsed?.message || text;
      code = typeof parsed?.code === "string" ? parsed.code : undefined;
    } catch {}
    const err: ApiError = new Error(message || `Request failed (${res.status})`);
    err.code = code;
    throw err;
  }
  return res.json();
}

type CapturedFix = { latitude: number; longitude: number; capturedAt: string };

// A fresh, high-accuracy fix taken at slide time and sent with the confirm.
// The server prefers this over the passively-tracked location, which is why a
// queued offline confirmation still passes the distance gate on replay.
async function captureFix(): Promise<CapturedFix | null> {
  try {
    if (Capacitor.isNativePlatform()) {
      const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
      return { latitude: pos.coords.latitude, longitude: pos.coords.longitude, capturedAt: new Date().toISOString() };
    }
    if (typeof navigator !== "undefined" && "geolocation" in navigator) {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }),
      );
      return { latitude: pos.coords.latitude, longitude: pos.coords.longitude, capturedAt: new Date().toISOString() };
    }
  } catch {
    /* no fix — the server falls back to the tracked location */
  }
  return null;
}

async function uploadProof(blob: Blob): Promise<string> {
  const form = new FormData();
  form.append("image", blob, "proof.jpg");
  const res = await fetch("/api/uploads/delivery-proof", { method: "POST", credentials: "include", body: form });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Upload failed (${res.status})`);
  }
  return ((await res.json()) as { imageUrl: string }).imageUrl;
}

const DELIVERY_PROOF_PHOTO = {
  uploadUrl: "/api/uploads/delivery-proof",
  formField: "image",
  responseUrlKey: "imageUrl",
  bodyField: "photoUrl",
} as const;

// A dropped connection surfaces as a fetch TypeError, not an HTTP status. Only
// these route a field action into the outbox — a real 4xx (distance mismatch,
// seller-not-ready) must still surface to the driver, not silently queue.
function isNetworkError(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  const msg = String((err as { message?: string })?.message || "").toLowerCase();
  return /failed to fetch|load failed|network|timed out|timeout/.test(msg);
}

function DriverSignup({ onSuccess }: { onSuccess: () => void }) {
  const [vehicleType, setVehicleType] = useState("");
  const [vehicleMake, setVehicleMake] = useState("");
  const [vehicleModel, setVehicleModel] = useState("");
  const [vehicleYear, setVehicleYear] = useState("");
  const [vehicleColor, setVehicleColor] = useState("");
  const [licensePlate, setLicensePlate] = useState("");
  const [licenseState, setLicenseState] = useState("");
  const [licenseExpiry, setLicenseExpiry] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [hasInsurance, setHasInsurance] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [maxMiles, setMaxMiles] = useState("15");
  const [gpsGranted, setGpsGranted] = useState(false);
  const [pushGranted, setPushGranted] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(false);
  const { toast } = useToast();

  const requestPermissions = async () => {
    setCheckingPermissions(true);
    try {
      // Request GPS
      let gpsOk = false;
      if (typeof Capacitor !== "undefined" && Capacitor.isNativePlatform()) {
        const { Geolocation } = await import("@capacitor/geolocation");
        const gpsPerm = await Geolocation.requestPermissions();
        gpsOk = gpsPerm.location === "granted" || gpsPerm.coarseLocation === "granted";
      } else if ("geolocation" in navigator) {
        try {
          await new Promise<GeolocationPosition>((resolve, reject) =>
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 })
          );
          gpsOk = true;
        } catch { gpsOk = false; }
      }
      setGpsGranted(gpsOk);
      if (!gpsOk) {
        toast({ title: "GPS required", description: "Please enable location access to deliver.", variant: "destructive" });
      }

      // Request Push
      let pushOk = false;
      if (typeof Capacitor !== "undefined" && Capacitor.isNativePlatform()) {
        const { PushNotifications } = await import("@capacitor/push-notifications");
        const pushPerm = await PushNotifications.requestPermissions();
        pushOk = pushPerm.receive === "granted";
      } else if ("Notification" in window) {
        const result = await Notification.requestPermission();
        pushOk = result === "granted";
      }
      setPushGranted(pushOk);
      if (!pushOk) {
        toast({ title: "Notifications required", description: "Please enable push notifications so you receive delivery requests.", variant: "destructive" });
      }
    } catch (err: any) {
      toast({ title: "Permission error", description: err?.message || "Could not check permissions", variant: "destructive" });
    } finally {
      setCheckingPermissions(false);
    }
  };

  const isComplete = vehicleType && vehicleMake.trim() && vehicleModel.trim() && vehicleColor.trim() &&
    licensePlate.trim() && licenseState.trim() && licenseExpiry && dateOfBirth && hasInsurance && agreeTerms && maxMiles &&
    gpsGranted && pushGranted;

  // Returns the first unmet requirement as a human message, or null when ready.
  // The submit button stays tappable and surfaces this so the user is never
  // stuck on a dead disabled button wondering what is missing (trap 10).
  const firstMissingRequirement = (): string | null => {
    if (!vehicleType) return "Choose your vehicle type.";
    if (!vehicleMake.trim()) return "Enter your vehicle make.";
    if (!vehicleModel.trim()) return "Enter your vehicle model.";
    if (!vehicleColor.trim()) return "Enter your vehicle color.";
    if (!licensePlate.trim()) return "Enter your license plate.";
    if (!licenseState.trim()) return "Enter your driver's license state.";
    if (!licenseExpiry) return "Enter your license expiration date.";
    if (!dateOfBirth) return "Enter your date of birth.";
    if (!hasInsurance) return "Confirm you have insurance.";
    if (!agreeTerms) return "Agree to the Independent Contractor terms.";
    if (!maxMiles) return "Set how far you are willing to drive.";
    if (!gpsGranted || !pushGranted) return "Tap Grant GPS and notification access above, then allow both.";
    return null;
  };

  const signup = useMutation({
    mutationFn: () =>
      apiFetch<unknown>("/api/driver-profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          vehicleType, vehicleMake, vehicleModel, vehicleYear, vehicleColor,
          licensePlate, driversLicenseState: licenseState, driversLicenseExpiry: licenseExpiry,
          dateOfBirth, hasInsurance: hasInsurance ? 1 : 0, maxMiles: Number(maxMiles),
        }),
      }),
    onSuccess: () => {
      toast({ title: "Welcome aboard!", description: "You are now a community driver." });
      onSuccess();
    },
    onError: (err: Error) => {
      toast({ title: "Signup failed", description: err.message, variant: "destructive" });
    },
  });

  return (
    <div className="flex flex-1 items-center justify-center px-4 py-8">
      <Card className="w-full max-w-md">
        <CardContent className="space-y-5 pt-6">
          <div className="flex flex-col items-center gap-2 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Truck className="h-6 w-6 text-primary" />
            </div>
            <h2 className="text-xl font-semibold">Become a community driver</h2>
            <p className="text-xs text-muted-foreground">
              Earn money delivering local food to your neighbors on your own schedule.
            </p>
          </div>

          <div className="space-y-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Driver info</p>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Date of birth *</Label>
                <Input type="date" value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">License state *</Label>
                <Input placeholder="NY" maxLength={2} value={licenseState} onChange={(e) => setLicenseState(e.target.value.toUpperCase())} />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">License expiration *</Label>
              <Input type="date" value={licenseExpiry} onChange={(e) => setLicenseExpiry(e.target.value)} />
            </div>

            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-2">Vehicle info</p>

            <div className="space-y-1">
              <Label className="text-xs">Vehicle type *</Label>
              <Select value={vehicleType} onValueChange={setVehicleType}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="car">Car</SelectItem>
                  <SelectItem value="suv">SUV</SelectItem>
                  <SelectItem value="truck">Truck</SelectItem>
                  <SelectItem value="van">Van</SelectItem>
                  <SelectItem value="bike">Bike / E-bike</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Make *</Label>
                <Input placeholder="Toyota" value={vehicleMake} onChange={(e) => setVehicleMake(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Model *</Label>
                <Input placeholder="Camry" value={vehicleModel} onChange={(e) => setVehicleModel(e.target.value)} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Year</Label>
                <Input placeholder="2020" maxLength={4} value={vehicleYear} onChange={(e) => setVehicleYear(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Color *</Label>
                <Input placeholder="Silver" value={vehicleColor} onChange={(e) => setVehicleColor(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Plate *</Label>
                <Input placeholder="ABC1234" value={licensePlate} onChange={(e) => setLicensePlate(e.target.value.toUpperCase())} />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Max delivery distance (miles)</Label>
              <Input type="number" min={1} max={100} value={maxMiles} onChange={(e) => setMaxMiles(e.target.value)} />
            </div>

            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-2">Required permissions</p>

            {!gpsGranted || !pushGranted ? (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Delivery drivers must have GPS and push notifications enabled so buyers can track deliveries and you receive delivery requests.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={checkingPermissions}
                  onClick={requestPermissions}
                >
                  {checkingPermissions ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <MapPin className="mr-2 h-4 w-4" />
                  )}
                  Grant GPS & notification access
                </Button>
                <div className="flex gap-3 text-xs">
                  <span className={gpsGranted ? "text-green-600" : "text-muted-foreground"}>
                    {gpsGranted ? "✓ GPS enabled" : "○ GPS required"}
                  </span>
                  <span className={pushGranted ? "text-green-600" : "text-muted-foreground"}>
                    {pushGranted ? "✓ Notifications enabled" : "○ Notifications required"}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex gap-3 text-xs text-green-600">
                <span>✓ GPS enabled</span>
                <span>✓ Notifications enabled</span>
              </div>
            )}

            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-2">Confirmations</p>

            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={hasInsurance} onChange={(e) => setHasInsurance(e.target.checked)} className="mt-1" />
              <span>I have valid auto insurance that covers this vehicle *</span>
            </label>

            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} className="mt-1" />
              <span>I confirm I am 18+, have a valid driver's license, and agree to the Independent Contractor terms *</span>
            </label>

            <Button
              className="w-full"
              disabled={signup.isPending}
              onClick={() => {
                const missing = firstMissingRequirement();
                if (missing) {
                  toast({ title: "One more step", description: missing, variant: "destructive" });
                  if (!gpsGranted || !pushGranted) requestPermissions();
                  return;
                }
                signup.mutate();
              }}
            >
              {signup.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Truck className="mr-2 h-4 w-4" />
              )}
              Sign up as driver
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AvailableRequests({ onAccepted }: { onAccepted?: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [declineTargetId, setDeclineTargetId] = useState<string | null>(null);
  const [scheduleTargetId, setScheduleTargetId] = useState<string | null>(null);

  const { data: requests = [], isLoading } = useQuery<DeliveryRequest[]>({
    queryKey: ["/api/delivery-requests/available"],
    queryFn: () => apiFetch<DeliveryRequest[]>("/api/delivery-requests/available"),
    refetchInterval: 30_000,
  });

  const accept = useMutation({
    mutationFn: ({ id, scheduledFor }: { id: string; scheduledFor: string }) =>
      apiFetch<unknown>(`/api/delivery-requests/${id}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scheduledFor }),
      }),
    onSuccess: () => {
      toast({ title: "Accepted!", description: "Switching to My Deliveries..." });
      queryClient.invalidateQueries({ queryKey: ["/api/delivery-requests/available"] });
      queryClient.invalidateQueries({ queryKey: ["/api/my/deliveries"] });
      queryClient.invalidateQueries({ queryKey: ["/api/driver-profile"] });
      setScheduleTargetId(null);
      onAccepted?.();
    },
    onError: (err: Error) => {
      toast({ title: "Could not accept", description: err.message, variant: "destructive" });
    },
  });

  async function acceptWithPermissionCheck(requestId: string) {
    let gpsOk = false;
    let pushOk = false;
    try {
      if (Capacitor.isNativePlatform()) {
        const { Geolocation } = await import("@capacitor/geolocation");
        const gp = await Geolocation.checkPermissions();
        gpsOk = gp.location === "granted" || gp.coarseLocation === "granted";
        const { PushNotifications } = await import("@capacitor/push-notifications");
        const pp = await PushNotifications.checkPermissions();
        pushOk = pp.receive === "granted";
      } else {
        gpsOk = true;
        pushOk = true;
      }
    } catch {
      gpsOk = true;
      pushOk = true;
    }
    if (!gpsOk) {
      toast({
        title: "GPS required",
        description: "Enable location access before accepting a delivery.",
        variant: "destructive",
      });
      return;
    }
    if (!pushOk) {
      toast({
        title: "Notifications required",
        description: "Enable push notifications before accepting a delivery.",
        variant: "destructive",
      });
      return;
    }
    setScheduleTargetId(requestId);
  }

  const autoAcceptTriggered = useRef(false);
  useEffect(() => {
    if (autoAcceptTriggered.current) return;
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const id = params.get("accept");
    if (!id) return;
    if (!requests.some((r) => r.id === id)) return;
    autoAcceptTriggered.current = true;
    params.delete("accept");
    const newSearch = params.toString();
    const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : "");
    window.history.replaceState(null, "", newUrl);
    void acceptWithPermissionCheck(id);
  }, [requests]);

  const scheduleTarget = scheduleTargetId
    ? requests.find((r) => r.id === scheduleTargetId)
    : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center gap-3 py-14 text-center">
          <Package className="h-10 w-10 text-muted-foreground" />
          <p className="font-medium">No available deliveries right now</p>
          <p className="text-sm text-muted-foreground">
            Check back soon -- new requests appear throughout the day.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {requests.map((req) => (
        <Card key={req.id}>
          <CardContent className="space-y-3 pt-5">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">
                    {req.buyerCity}, {req.buyerState}
                  </span>
                </div>
                <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <Truck className="h-3.5 w-3.5" />
                    ~{req.drivingMiles ?? req.estimatedMiles} mi driving
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Package className="h-3.5 w-3.5" />
                    {req.stops?.length ?? 0} stop{(req.stops?.length ?? 0) !== 1 ? "s" : ""}
                  </span>
                  {req.estimatedMinutes != null && req.estimatedMinutes > 0 && (
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      ~{req.estimatedMinutes} min total
                    </span>
                  )}
                </div>
              </div>

              <div className="text-right">
                {req.isFounderDelivery ? (
                  <div className="text-sm font-semibold text-primary">Volunteer</div>
                ) : (
                  <>
                    <div className="flex items-center gap-1 text-lg font-semibold text-primary">
                      <DollarSign className="h-4 w-4" />
                      {Number(req.driverPay).toFixed(2)}
                    </div>
                    {req.netHourlyRate != null && req.estimatedMinutes != null && req.estimatedMinutes > 0 && (
                      <div className="text-[11px] text-muted-foreground">
                        ~${req.netHourlyRate}/hr after gas
                      </div>
                    )}
                    {req.vehicleCost != null && req.vehicleCost > 0 && (
                      <div className="text-[10px] text-muted-foreground">
                        est. ${req.vehicleCost.toFixed(2)} gas/wear
                      </div>
                    )}
                  </>
                )}
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  by {formatDeliverBy(req)}
                </div>
              </div>
            </div>

            {req.isFounderDelivery && (
              <div className="rounded-md border border-primary/30 bg-primary/5 p-2.5 text-[11px] text-muted-foreground">
                This is a free founder delivery, not a paid run. Volunteer to deliver it for
                a neighbor. If no one volunteers, the app creator will make the delivery.
              </div>
            )}

            <div className="flex flex-col gap-2 sm:flex-row sm:w-auto">
              <Button
                className="w-full sm:w-auto"
                size="sm"
                disabled={accept.isPending}
                onClick={() => {
                  void acceptWithPermissionCheck(req.id);
                }}
              >
                {accept.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Check className="mr-2 h-4 w-4" />
                )}
                Accept
              </Button>
              <Button
                className="w-full sm:w-auto"
                size="sm"
                variant="outline"
                onClick={() => setDeclineTargetId(req.id)}
                data-testid={`decline-available-${req.id}`}
              >
                Decline
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}

      <DeclineDeliveryDialog
        open={declineTargetId != null}
        onOpenChange={(open) => !open && setDeclineTargetId(null)}
        deliveryRequestId={declineTargetId}
        onDeclined={() => {
          queryClient.invalidateQueries({ queryKey: ["/api/delivery-requests/available"] });
          queryClient.invalidateQueries({ queryKey: ["/api/my/notifications"] });
        }}
      />

      <ScheduleAcceptDialog
        request={scheduleTarget ?? null}
        onClose={() => setScheduleTargetId(null)}
        onConfirm={(scheduledFor) => {
          if (!scheduleTargetId) return;
          accept.mutate({ id: scheduleTargetId, scheduledFor });
        }}
        isSubmitting={accept.isPending}
      />
    </div>
  );
}

type AvailableSlot = {
  start: string;
  end: string;
  label: string;
};

function ScheduleAcceptDialog({
  request,
  onClose,
  onConfirm,
  isSubmitting,
}: {
  request: DeliveryRequest | null;
  onClose: () => void;
  onConfirm: (scheduledFor: string) => void;
  isSubmitting: boolean;
}) {
  const [selectedStart, setSelectedStart] = useState<string | null>(null);

  const { data: slotsData, isLoading: slotsLoading } = useQuery<{ slots: AvailableSlot[] }>({
    queryKey: ["/api/delivery-requests", request?.id, "available-windows"],
    queryFn: () => apiFetch<{ slots: AvailableSlot[] }>(`/api/delivery-requests/${request!.id}/available-windows`),
    enabled: Boolean(request?.id),
  });
  const slots = slotsData?.slots ?? [];

  useEffect(() => {
    if (request) {
      setSelectedStart(slots[0]?.start ?? null);
    }
  }, [request?.id, slots.length]);

  // Group slots by calendar day for display.
  const slotsByDay = slots.reduce<Record<string, AvailableSlot[]>>((acc, slot) => {
    const d = new Date(slot.start);
    const key = d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
    (acc[key] ||= []).push(slot);
    return acc;
  }, {});

  function handleConfirm() {
    if (selectedStart) onConfirm(selectedStart);
  }

  return (
    <Dialog open={request != null} onOpenChange={(open) => !open && !isSubmitting && onClose()}>
      <DialogContent className="flex max-h-[calc(100dvh-(var(--safe-area-top,0px)*2)-2rem)] flex-col sm:max-w-md">
        <DialogHeader className="shrink-0">
          <DialogTitle>Schedule this delivery</DialogTitle>
          <DialogDescription>
            These are the seller's pickup days that fall inside the buyer's delivery window, so the choices can be limited. Pick a 2-hour window — the seller and buyer are both notified once you confirm.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-3 overflow-y-auto pr-1">
          {slotsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : slots.length === 0 ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800/40 dark:bg-amber-950/30">
              No pickup windows available in the next 7 days. The seller hasn't published any availability that fits this order's deadline. Decline this request and let them know to update their schedule.
            </div>
          ) : (
            Object.entries(slotsByDay).map(([day, daySlots]) => (
              <div key={day}>
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{day}</div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {daySlots.map((slot) => {
                    const start = new Date(slot.start);
                    const end = new Date(slot.end);
                    const startTime = start.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
                    const endTime = end.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
                    const isSelected = selectedStart === slot.start;
                    return (
                      <button
                        key={slot.start}
                        type="button"
                        onClick={() => setSelectedStart(slot.start)}
                        className={`rounded-md border p-2 text-xs transition ${
                          isSelected
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-input bg-background hover:bg-accent"
                        }`}
                      >
                        <span className="font-medium">{startTime}</span>
                        <span className="opacity-80"> – {endTime}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        <DialogFooter className="shrink-0 gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isSubmitting || !selectedStart}>
            {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Confirm & accept
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MyDeliveries() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: deliveries = [], isLoading } = useQuery<DeliveryRequest[]>({
    queryKey: ["/api/my/deliveries"],
    queryFn: () => apiFetch<DeliveryRequest[]>("/api/my/deliveries"),
    refetchInterval: 30_000,
  });

  // Stop sharing (and drop the started flag) once a run is no longer live.
  useEffect(() => {
    const live = new Set(
      deliveries
        .filter((d) => !["delivered", "completed", "cancelled"].includes(d.status))
        .map((d) => d.id),
    );
    let changed = false;
    const next = new Set(startedIds);
    for (const id of startedIds) {
      if (!live.has(id)) {
        next.delete(id);
        changed = true;
      }
    }
    if (changed) persistStarted(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveries]);

  // Armed when the server reports GPS disagreeing with the stop's coordinates.
  // The next slide sends overrideDistance so a driver who is physically present
  // is never stuck behind a bad geocode or a slow fix.
  const [distanceMismatch, setDistanceMismatch] = useState<"pickup" | "dropoff" | null>(null);
  // Covers the capture-fix + upload phase (before any mutation starts) and the
  // offline-queue path, so the slide shows a spinner the whole time.
  const [submittingPickup, setSubmittingPickup] = useState(false);
  const [submittingDropoff, setSubmittingDropoff] = useState(false);
  const { pending: outboxPending, failed: outboxFailed } = useOutbox();

  const pickupStop = useMutation({
    mutationFn: ({
      deliveryId,
      stopId,
      photoUrl,
      overrideDistance,
      fix,
    }: { deliveryId: string; stopId: string; photoUrl?: string; overrideDistance?: boolean; fix?: CapturedFix | null }) =>
      apiFetch<unknown>(`/api/delivery-requests/${deliveryId}/pickup/${stopId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...(photoUrl ? { photoUrl } : {}),
          ...(overrideDistance ? { overrideDistance: true } : {}),
          ...(fix ?? {}),
        }),
      }),
    onSuccess: () => {
      setDistanceMismatch(null);
      toast({ title: "Pickup confirmed" });
      queryClient.invalidateQueries({ queryKey: ["/api/my/deliveries"] });
    },
    onError: (err: Error) => {
      // A dropped connection is handled by the caller (queued to the outbox);
      // don't show a scary error for it.
      if (isNetworkError(err)) return;
      // The server asks for a second deliberate slide when GPS disagrees with
      // the stop's coordinates. Arm the override so the next slide goes through.
      if ((err as ApiError).code === "distance_mismatch") {
        setDistanceMismatch("pickup");
      }
      toast({ title: "Check your location", description: err.message, variant: "destructive" });
    },
  });

  const [pickingUp, setPickingUp] = useState<{
    deliveryId: string;
    stop: {
      id: string;
      sellerName: string;
      sellerPhone?: string | null;
      city?: string | null;
      state?: string | null;
      lat?: number | string | null;
      lng?: number | string | null;
      orderId?: string | null;
    };
  } | null>(null);

  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [driverPos, setDriverPos] = useState<{ lat: number; lng: number } | null>(null);
  // Deliveries the driver has actively started (tapped "Start Route"). Location
  // sharing runs only for these, so an accepted-but-not-started run — e.g. one
  // scheduled for the next morning — never tracks the driver in the background.
  // Persisted so a reload mid-run keeps sharing; pruned when the run completes.
  const [startedIds, setStartedIds] = useState<Set<string>>(() => {
    try {
      const raw = window.localStorage.getItem("stoop:startedDeliveries");
      return new Set<string>(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set<string>();
    }
  });
  const persistStarted = (next: Set<string>) => {
    setStartedIds(next);
    try {
      window.localStorage.setItem("stoop:startedDeliveries", JSON.stringify([...next]));
    } catch {
      /* private mode */
    }
  };
  const markStarted = (id: string) => {
    if (startedIds.has(id)) return;
    const next = new Set(startedIds);
    next.add(id);
    persistStarted(next);
  };
  const stopSharing = (id: string) => {
    if (!startedIds.has(id)) return;
    const next = new Set(startedIds);
    next.delete(id);
    persistStarted(next);
  };

  const confirmDelivery = useMutation({
    mutationFn: (input: { deliveryId: string; photoUrl: string; notes?: string; overrideDistance?: boolean; fix?: CapturedFix | null }) =>
      apiFetch<unknown>(`/api/delivery-requests/${input.deliveryId}/deliver`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          photoUrl: input.photoUrl,
          notes: input.notes || undefined,
          ...(input.overrideDistance ? { overrideDistance: true } : {}),
          ...(input.fix ?? {}),
        }),
      }),
    onSuccess: (_data, input) => {
      setDistanceMismatch(null);
      toast({ title: "Delivery confirmed!", description: "Great job!" });
      queryClient.invalidateQueries({ queryKey: ["/api/my/deliveries"] });
      queryClient.invalidateQueries({ queryKey: ["/api/driver-profile"] });
      setConfirmingId(null);

      // Auto-log the miles driven on this run as a mileage expense (tax
      // write-off) — the GPS sender accumulated them under this key.
      try {
        const key = deliveryMilesKey(input.deliveryId);
        const miles = Number(window.localStorage.getItem(key));
        window.localStorage.removeItem(key);
        if (Number.isFinite(miles) && miles >= 0.1) {
          const rounded = Math.round(miles * 100) / 100;
          const deduction = mileageDeduction(rounded);
          void fetch("/api/seller/expenses", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              expenseDate: new Date().toISOString().slice(0, 10),
              category: MILEAGE_CATEGORY,
              vendor: "Stoop delivery",
              amount: deduction.toFixed(2),
              miles: rounded.toFixed(2),
              notes: `${rounded.toFixed(1)} mi community delivery @ ${IRS_MILEAGE_RATE * 100}¢/mi`,
            }),
          }).then((res) => {
            if (!res.ok) return;
            queryClient.invalidateQueries({ queryKey: ["/api/seller/expenses"] });
            toast({
              title: "Mileage logged",
              description: `${rounded.toFixed(1)} mi → $${deduction.toFixed(2)} deduction in your expenses.`,
            });
          }).catch(() => {});
        }
      } catch { /* mileage logging is best-effort */ }
    },
    onError: (err: Error) => {
      if (isNetworkError(err)) return;
      if ((err as ApiError).code === "distance_mismatch") {
        setDistanceMismatch("dropoff");
      }
      toast({ title: "Check your location", description: err.message, variant: "destructive" });
    },
  });

  // When the outbox drains a queued confirmation, pull the fresh delivery state.
  useEffect(() => {
    const onSync = () => queryClient.invalidateQueries({ queryKey: ["/api/my/deliveries"] });
    window.addEventListener("stoop-outbox-sync", onSync);
    return () => window.removeEventListener("stoop-outbox-sync", onSync);
  }, [queryClient]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (deliveries.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center gap-3 py-14 text-center">
          <Truck className="h-10 w-10 text-muted-foreground" />
          <p className="font-medium">No deliveries yet</p>
          <p className="text-sm text-muted-foreground">
            Accept a delivery from the Available tab to get started.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {outboxPending > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800/40 dark:bg-amber-950/30 dark:text-amber-300">
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
          <span>
            {outboxPending} confirmation{outboxPending === 1 ? "" : "s"} saved offline — sending automatically when you're back in service.
          </span>
        </div>
      )}
      {outboxFailed > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-800/40 dark:bg-red-950/30 dark:text-red-300">
          <span className="flex-1">
            {outboxFailed} saved confirmation{outboxFailed === 1 ? "" : "s"} couldn't be sent.
          </span>
          <button type="button" className="font-medium underline underline-offset-2" onClick={() => void retryFailedOutbox()}>
            Retry
          </button>
        </div>
      )}
      {deliveries.map((del) => {
        const isCompleted = del.status === "delivered" || del.status === "completed";
        const allPickedUp =
          del.stops?.length > 0 && del.stops.every((s) => isStopPickedUp(s));

        return (
          <Card key={del.id}>
            <CardContent className="space-y-3 pt-5">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">
                      {del.buyerCity}, {del.buyerState}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    by {formatDeliverBy(del)}
                  </div>
                  {del.scheduledFor && (
                    <div className="flex items-center gap-1 text-xs font-medium text-primary">
                      <Clock className="h-3 w-3" />
                      Out for delivery: {formatScheduledFor(del.scheduledFor)}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {del.isFounderDelivery ? (
                    <span className="text-sm font-semibold text-primary">Volunteer</span>
                  ) : (
                    <span className="flex items-center gap-1 font-semibold text-primary">
                      <DollarSign className="h-4 w-4" />
                      {Number(del.driverPay).toFixed(2)}
                    </span>
                  )}
                  {isCompleted && (
                    <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                      <Check className="mr-1 h-3 w-3" />
                      Completed
                    </Badge>
                  )}
                </div>
              </div>

              {!isCompleted && (del.status === "accepted" || del.status === "in_progress") && (
                <div className="flex flex-col gap-1">
                  <DriverGPSSender
                    deliveryRequestId={del.id}
                    isActive={(startedIds.has(del.id) || del.status === "in_progress") && !isCompleted}
                    onPosition={setDriverPos}
                  />
                  {startedIds.has(del.id) && del.status !== "in_progress" && (
                    <button
                      type="button"
                      onClick={() => stopSharing(del.id)}
                      className="self-start text-[11px] text-muted-foreground underline underline-offset-2"
                    >
                      Stop sharing my location
                    </button>
                  )}
                </div>
              )}

              {!isCompleted && del.stops && del.stops.length > 0 && (() => {
                const pendingStops = del.stops.filter((s) => !isStopPickedUp(s));
                const waypoints = del.stops
                  .filter((s) => !isStopPickedUp(s) && s.lat && s.lng && Math.abs(Number(s.lat)) > 0.1)
                  .map((s) => `${s.lat},${s.lng}`);
                const buyerDest = del.buyerLat && del.buyerLng && Math.abs(Number(del.buyerLat)) > 0.1
                  ? `${del.buyerLat},${del.buyerLng}`
                  : encodeURIComponent(`${del.buyerCity} ${del.buyerState}`);
                const fullRouteUrl = waypoints.length > 0
                  ? `https://www.google.com/maps/dir/?api=1&destination=${buyerDest}&waypoints=${waypoints.join("|")}&travelmode=driving`
                  : `https://www.google.com/maps/dir/?api=1&destination=${buyerDest}&travelmode=driving`;

                // Can't begin a delivery before the seller marks it ready, and
                // never before the scheduled pickup window (which is itself >=
                // the buyer's requested date). Keeps a driver from starting days
                // early or before the food is made.
                const anyReady = pendingStops.some((s) => s.sellerReady !== false);
                const earliestPickupMs = Math.max(
                  del.requestedDeliveryDate ? new Date(del.requestedDeliveryDate).getTime() : 0,
                  del.scheduledFor ? new Date(del.scheduledFor).getTime() : 0,
                );
                // A seller who has marked the order ready lets the driver start
                // early — the whole point of "too early" is not grabbing it
                // before it's prepped, which no longer applies once it's ready.
                const sellerMarkedReady = ["ready_for_pickup", "out_for_delivery"].includes(
                  String(del.orderStatus || "").toLowerCase(),
                );
                const tooEarly =
                  earliestPickupMs > 0 && Date.now() < earliestPickupMs && !sellerMarkedReady;
                const windowLabel = del.scheduledFor ? formatScheduledFor(del.scheduledFor) : null;
                const canStart = anyReady && !tooEarly;

                // GPS arrival: when the driver reaches a ready, pending pickup,
                // surface a prominent prompt to confirm it (instead of only
                // gating the tap after the fact).
                const arrivedStop =
                  driverPos && !tooEarly
                    ? pendingStops.find(
                        (s) =>
                          s.sellerReady !== false &&
                          s.lat != null &&
                          s.lng != null &&
                          Math.abs(Number(s.lat)) > 0.1 &&
                          metersBetween(driverPos.lat, driverPos.lng, Number(s.lat), Number(s.lng)) <=
                            ARRIVAL_RADIUS_M,
                      )
                    : null;

                return (
                  <>
                    {arrivedStop && (
                      <button
                        type="button"
                        onClick={() =>
                          setPickingUp({
                            deliveryId: del.id,
                            stop: {
                              id: arrivedStop.id,
                              sellerName: arrivedStop.sellerName,
                              sellerPhone: (arrivedStop as any).sellerPhone ?? null,
                              city: arrivedStop.city,
                              state: arrivedStop.state,
                              lat: arrivedStop.lat,
                              lng: arrivedStop.lng,
                              orderId: (arrivedStop as any).orderId ?? del.orderId ?? null,
                            },
                          })
                        }
                        className="flex w-full items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-3 text-sm font-semibold text-white shadow-md animate-pulse"
                      >
                        <MapPin className="h-5 w-5" />
                        You've arrived at {arrivedStop.sellerName} — confirm pickup
                      </button>
                    )}
                    {pendingStops.length > 0 && (
                      canStart ? (
                        <a
                          href={fullRouteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => markStarted(del.id)}
                          className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-md"
                        >
                          <MapPin className="h-5 w-5" />
                          Start Route — {pendingStops.length} pickup{pendingStops.length === 1 ? "" : "s"} + delivery
                        </a>
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            toast({
                              title: tooEarly ? "Not time yet" : "Waiting on the seller",
                              description: tooEarly
                                ? `You can start this delivery at your scheduled pickup window${windowLabel ? ` (${windowLabel})` : ""}.`
                                : "The seller hasn't marked the order ready for pickup yet. You can start once they do.",
                            })
                          }
                          className="flex items-center justify-center gap-2 rounded-lg bg-muted px-4 py-3 text-sm font-semibold text-muted-foreground"
                        >
                          <MapPin className="h-5 w-5" />
                          {tooEarly
                            ? `Starts ${windowLabel ?? "at your scheduled window"}`
                            : "Waiting for seller to mark ready"}
                        </button>
                      )
                    )}

                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-muted-foreground">
                        {allPickedUp ? "All pickups complete — deliver to buyer" : `Pickup progress (${del.stops.filter((s) => isStopPickedUp(s)).length}/${del.stops.length})`}
                      </p>
                      {del.stops.map((stop, i) => (
                        <div
                          key={stop.id}
                          className={`flex items-center gap-2 rounded-md p-2 text-sm ${isStopPickedUp(stop) ? "bg-green-50 dark:bg-green-950/20" : "bg-muted/40"}`}
                        >
                          <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${isStopPickedUp(stop) ? "bg-green-500 text-white" : "bg-muted text-muted-foreground"}`}>
                            {isStopPickedUp(stop) ? <Check className="h-3.5 w-3.5" /> : i + 1}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className={`font-medium ${isStopPickedUp(stop) ? "line-through text-muted-foreground" : ""}`}>{stop.sellerName}</p>
                            <p className="text-[11px] text-muted-foreground">{stop.city}, {stop.state}</p>
                          </div>
                          {!isStopPickedUp(stop) && (
                            <button
                              type="button"
                              onClick={() => {
                                if (tooEarly) {
                                  toast({
                                    title: "Not time yet",
                                    description: `You can pick this up at your scheduled window${windowLabel ? ` (${windowLabel})` : ""}.`,
                                  });
                                  return;
                                }
                                if (stop.sellerReady === false) {
                                  toast({
                                    title: "Not ready yet",
                                    description:
                                      "This seller hasn't marked the order ready for pickup yet. You can pick it up once they do.",
                                  });
                                  return;
                                }
                                setPickingUp({
                                  deliveryId: del.id,
                                  stop: {
                                    id: stop.id,
                                    sellerName: stop.sellerName,
                                    sellerPhone: (stop as any).sellerPhone ?? null,
                                    city: stop.city,
                                    state: stop.state,
                                    lat: stop.lat,
                                    lng: stop.lng,
                                    orderId: (stop as any).orderId ?? del.orderId ?? null,
                                  },
                                });
                              }}
                              className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-medium ${
                                tooEarly || stop.sellerReady === false
                                  ? "border-muted-foreground/20 bg-muted text-muted-foreground"
                                  : "border-primary/30 bg-primary/10 text-primary"
                              }`}
                            >
                              {tooEarly ? "Not yet" : stop.sellerReady === false ? "Awaiting seller" : "Confirm pickup"}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}

              {del.stops && del.stops.length > 0 && (
                <>
                  <DeliveryRouteMap
                    stops={del.stops.map((s, i) => ({
                      label: `Pickup ${i + 1}: ${s.sellerName}`,
                      lat: s.lat,
                      lng: s.lng,
                      status: isStopPickedUp(s) ? "picked_up" : "pending",
                    }))}
                    destination={{
                      label: `Drop-off: ${del.buyerCity}, ${del.buyerState}`,
                      lat: del.buyerLat,
                      lng: del.buyerLng,
                    }}
                    driverLocation={isCompleted ? null : driverPos}
                    height={180}
                  />
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    {del.stops.map((s, i) => (
                      <span key={s.id}>
                        <span className="font-semibold text-blue-600">{i + 1}</span> Pickup: {s.sellerName}
                      </span>
                    ))}
                    <span>
                      <span className="font-semibold text-red-600">{del.stops.length + 1}</span> Drop-off: {del.buyerCity}, {del.buyerState}
                    </span>
                  </div>
                </>
              )}

              {!isCompleted && allPickedUp && (() => {
                const buyerDest = del.buyerLat && del.buyerLng && Math.abs(Number(del.buyerLat)) > 0.1
                  ? `${del.buyerLat},${del.buyerLng}`
                  : encodeURIComponent(`${del.buyerCity} ${del.buyerState}`);
                const arrivedAtBuyer =
                  driverPos &&
                  del.buyerLat != null &&
                  del.buyerLng != null &&
                  Math.abs(Number(del.buyerLat)) > 0.1 &&
                  metersBetween(driverPos.lat, driverPos.lng, Number(del.buyerLat), Number(del.buyerLng)) <=
                    ARRIVAL_RADIUS_M;
                return arrivedAtBuyer ? (
                  <button
                    type="button"
                    onClick={() => setConfirmingId(del.id)}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-3 text-sm font-semibold text-white shadow-md animate-pulse"
                  >
                    <MapPin className="h-5 w-5" />
                    You've arrived — confirm delivery
                  </button>
                ) : (
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${buyerDest}&travelmode=driving`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => markStarted(del.id)}
                    className="flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-md"
                  >
                    <MapPin className="h-5 w-5" />
                    Navigate to delivery
                  </a>
                );
              })()}

              <div className="flex flex-wrap gap-2">
                {del.orderId && (
                  <Button asChild variant="outline" size="sm">
                    <a href={`/orders/${del.orderId}`}>View Order Details</a>
                  </Button>
                )}
                {!isCompleted && (
                  <Button
                    className="sm:w-auto"
                    size="sm"
                    disabled={!allPickedUp}
                    onClick={() => setConfirmingId(del.id)}
                  >
                    <Check className="mr-2 h-4 w-4" />
                    Confirm Delivery
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}

      <ConfirmDeliveryDialog
        open={confirmingId != null}
        onOpenChange={(open) => !open && setConfirmingId(null)}
        deliveryRequestId={confirmingId}
        isSubmitting={confirmDelivery.isPending || submittingDropoff}
        onConfirm={async ({ deliveryId, photoBlob, notes }) => {
          setSubmittingDropoff(true);
          try {
            const overrideDistance = distanceMismatch === "dropoff";
            const fix = await captureFix();
            const queueIt = async () => {
              await enqueueOutbox({
                kind: "dropoff",
                label: "Delivery drop-off",
                url: `/api/delivery-requests/${deliveryId}/deliver`,
                method: "POST",
                body: {
                  ...(notes ? { notes } : {}),
                  ...(overrideDistance ? { overrideDistance: true } : {}),
                  ...(fix ?? {}),
                },
                photo: { blob: photoBlob, ...DELIVERY_PROOF_PHOTO },
              });
              // Book the run's mileage deduction too — the online path logs it
              // in confirmDelivery.onSuccess, which never runs when queued.
              try {
                const key = deliveryMilesKey(deliveryId);
                const miles = Number(window.localStorage.getItem(key));
                if (Number.isFinite(miles) && miles >= 0.1) {
                  const rounded = Math.round(miles * 100) / 100;
                  await enqueueOutbox({
                    kind: "mileage",
                    label: "Delivery mileage",
                    url: "/api/seller/expenses",
                    method: "POST",
                    body: {
                      expenseDate: new Date().toISOString().slice(0, 10),
                      category: MILEAGE_CATEGORY,
                      vendor: "Stoop delivery",
                      amount: mileageDeduction(rounded).toFixed(2),
                      miles: rounded.toFixed(2),
                      notes: `${rounded.toFixed(1)} mi community delivery @ ${IRS_MILEAGE_RATE * 100}¢/mi`,
                    },
                  });
                  window.localStorage.removeItem(key);
                }
              } catch {
                /* mileage is best-effort */
              }
            };
            const savedOffline = (lostSignal: boolean) => {
              toast({
                title: "Saved offline",
                description: `${lostSignal ? "You lost signal — this" : "This"} delivery will send automatically when you're back in service.`,
              });
              // Disarm, or the next stop's very first slide would carry an
              // override the driver never deliberately confirmed.
              setDistanceMismatch(null);
              setConfirmingId(null);
            };
            if (typeof navigator !== "undefined" && navigator.onLine === false) {
              await queueIt();
              savedOffline(false);
              return;
            }
            let photoUrl: string;
            try {
              photoUrl = await uploadProof(photoBlob);
            } catch (err) {
              if (isNetworkError(err)) {
                await queueIt();
                savedOffline(true);
              } else {
                toast({ title: "Couldn't upload photo", description: (err as Error)?.message || "Try again.", variant: "destructive" });
              }
              return;
            }
            try {
              await confirmDelivery.mutateAsync({ deliveryId, photoUrl, notes, overrideDistance, fix });
            } catch (err) {
              if (isNetworkError(err)) {
                await queueIt();
                savedOffline(true);
              }
              // Non-network errors were surfaced by onError; keep the dialog open.
            }
          } finally {
            setSubmittingDropoff(false);
          }
        }}
      />

      <PickupStopDialog
        open={pickingUp != null}
        onOpenChange={(open) => !open && setPickingUp(null)}
        stop={pickingUp?.stop ?? null}
        isSubmitting={pickupStop.isPending || submittingPickup}
        onConfirm={async ({ stopId, photoBlob }) => {
          if (!pickingUp) return;
          const deliveryId = pickingUp.deliveryId;
          const sellerName = pickingUp.stop.sellerName;
          setSubmittingPickup(true);
          try {
            const overrideDistance = distanceMismatch === "pickup";
            const fix = await captureFix();
            const queueIt = () =>
              enqueueOutbox({
                kind: "pickup",
                label: `Pickup from ${sellerName}`,
                url: `/api/delivery-requests/${deliveryId}/pickup/${stopId}`,
                method: "POST",
                body: {
                  ...(overrideDistance ? { overrideDistance: true } : {}),
                  ...(fix ?? {}),
                },
                photo: { blob: photoBlob, ...DELIVERY_PROOF_PHOTO },
              });
            const savedOffline = (lostSignal: boolean) => {
              toast({
                title: "Saved offline",
                description: `${lostSignal ? "You lost signal — this" : "This"} pickup will send automatically when you're back in service.`,
              });
              // Disarm, or the next stop's very first slide would carry an
              // override the driver never deliberately confirmed.
              setDistanceMismatch(null);
              setPickingUp(null);
            };
            if (typeof navigator !== "undefined" && navigator.onLine === false) {
              await queueIt();
              savedOffline(false);
              return;
            }
            let photoUrl: string;
            try {
              photoUrl = await uploadProof(photoBlob);
            } catch (err) {
              if (isNetworkError(err)) {
                await queueIt();
                savedOffline(true);
              } else {
                toast({ title: "Couldn't upload photo", description: (err as Error)?.message || "Try again.", variant: "destructive" });
              }
              return;
            }
            try {
              await pickupStop.mutateAsync({ deliveryId, stopId, photoUrl, overrideDistance, fix });
              setPickingUp(null);
            } catch (err) {
              if (isNetworkError(err)) {
                await queueIt();
                savedOffline(true);
              }
              // Non-network errors were surfaced by onError; keep the dialog open.
            }
          } finally {
            setSubmittingPickup(false);
          }
        }}
      />
    </div>
  );
}

export default function Deliveries(props: { embedded?: boolean } = {}) {
  // Gate the entire deliveries experience on push + GPS. This fires for both
  // the /deliveries route and the embedded tab inside /orders, so there's no
  // way to view or interact with deliveries without both permissions.
  return (
    <PermissionsGate
      requirePush
      requireGps
      title="Enable notifications and GPS to drive"
      description="Drivers need push notifications for new delivery requests and GPS to share their location along the route."
    >
      <DeliveriesImpl {...props} />
    </PermissionsGate>
  );
}

function DeliveriesImpl({ embedded }: { embedded?: boolean } = {}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeDeliveryTab, setActiveDeliveryTab] = useState(() =>
    new URLSearchParams(window.location.search).get("tab") === "my" ? "my" : "available",
  );

  const {
    data: profileData,
    isLoading: profileLoading,
  } = useQuery<{ driverProfile: DriverProfile | null }>({
    queryKey: ["/api/driver-profile"],
    queryFn: () => apiFetch<{ driverProfile: DriverProfile | null }>("/api/driver-profile"),
  });

  const profile = profileData?.driverProfile ?? null;

  const toggleActive = useMutation({
    mutationFn: (isActive: boolean) =>
      apiFetch<unknown>("/api/driver-profile/active", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isActive }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/driver-profile"] });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  if (profileLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        {!embedded && <Header />}
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        {!embedded && <Header />}
        <DriverSignup
          onSuccess={() =>
            queryClient.invalidateQueries({ queryKey: ["/api/driver-profile"] })
          }
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {!embedded && <Header />}

      <main className="container mx-auto flex-1 px-4 py-6">
        <div className="mx-auto max-w-4xl space-y-6">
          {/* Title + active toggle */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold sm:text-3xl">Deliveries</h1>
              <p className="text-sm text-muted-foreground">
                Manage your delivery requests and earnings.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Label htmlFor="active-toggle" className="text-sm">
                Available for deliveries
              </Label>
              <Switch
                id="active-toggle"
                checked={profile.isActive}
                onCheckedChange={(checked) => toggleActive.mutate(checked)}
                disabled={toggleActive.isPending}
              />
            </div>
          </div>

          {/* Stats bar */}
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardContent className="flex items-center gap-3 py-4">
                <Package className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-bold">{profile.totalDeliveries ?? 0}</p>
                  <p className="text-xs text-muted-foreground">Total deliveries</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex items-center gap-3 py-4">
                <DollarSign className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-bold">
                    ${Number(profile.totalEarnings ?? 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-muted-foreground">Lifetime earnings</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs value={activeDeliveryTab} onValueChange={setActiveDeliveryTab} className="w-full">
            <TabsList className="w-full">
              <TabsTrigger value="available" className="flex-1">
                Available
              </TabsTrigger>
              <TabsTrigger value="my" className="flex-1">
                My Deliveries
              </TabsTrigger>
            </TabsList>

            <TabsContent value="available" className="mt-4">
              <AvailableRequests onAccepted={() => setActiveDeliveryTab("my")} />
            </TabsContent>

            <TabsContent value="my" className="mt-4">
              <MyDeliveries />
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
